import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Plus, Upload, MessageSquare, FileText, LogOut, User, FolderOpen, Send, 
  Image as ImageIcon, CheckCircle, Clock, AlertCircle, Edit, Trash2, X,
  Lightbulb, Phone, ExternalLink, Loader2, ChevronRight, Building2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export const ClientDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState({});
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Forms
  const [showNewProject, setShowNewProject] = useState(false);
  const [showAddIdea, setShowAddIdea] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({
    project_name: '',
    project_type: '',
    property_address: '',
    description: '',
    budget_range: ''
  });
  const [newIdea, setNewIdea] = useState({ text: '', images: [] });
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('clientLoggedIn');
    if (!isLoggedIn) {
      navigate('/client/login');
      return;
    }
    
    setClientInfo({
      id: localStorage.getItem('clientId'),
      name: localStorage.getItem('clientName'),
      email: localStorage.getItem('clientEmail'),
      phone: localStorage.getItem('clientPhone')
    });
    
    fetchProjects();
    fetchWhatsAppNumber();
  }, [navigate]);

  const fetchProjects = async () => {
    const clientId = localStorage.getItem('clientId');
    if (!clientId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/architecture-projects?client_id=${clientId}`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setLoading(false);
  };

  const fetchWhatsAppNumber = async () => {
    try {
      const response = await fetch(`${API_URL}/api/config/whatsapp`);
      if (response.ok) {
        const data = await response.json();
        setWhatsappNumber(data.whatsapp_number);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clientLoggedIn');
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientPhone');
    toast({ title: "Logged out successfully" });
    navigate('/client/login');
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectForm.project_name || !newProjectForm.project_type) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    try {
      const projectData = {
        ...newProjectForm,
        client_id: clientInfo.id,
        client_name: clientInfo.name,
        client_email: clientInfo.email,
        client_phone: clientInfo.phone
      };

      const response = await fetch(`${API_URL}/api/architecture-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData)
      });

      if (response.ok) {
        const data = await response.json();
        toast({ title: "Project Created!", description: "Your project has been saved" });
        setShowNewProject(false);
        setNewProjectForm({ project_name: '', project_type: '', property_address: '', description: '', budget_range: '' });
        fetchProjects();
        
        // Open WhatsApp with project details
        openWhatsApp(`New Project Created!\n\nProject: ${newProjectForm.project_name}\nType: ${newProjectForm.project_type}\nAddress: ${newProjectForm.property_address || 'Not specified'}\nBudget: ${newProjectForm.budget_range || 'Not specified'}\n\nDescription: ${newProjectForm.description || 'No description'}`);
      }
    } catch (error) {
      toast({ title: "Error creating project", variant: "destructive" });
    }
  };

  const handleUpdateProject = async () => {
    if (!selectedProject) return;

    try {
      const response = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_name: selectedProject.project_name,
          description: selectedProject.description,
          property_address: selectedProject.property_address,
          budget_range: selectedProject.budget_range,
          status: selectedProject.status
        })
      });

      if (response.ok) {
        toast({ title: "Project Updated!" });
        setShowEditProject(false);
        fetchProjects();
      }
    } catch (error) {
      toast({ title: "Error updating project", variant: "destructive" });
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await fetch(`${API_URL}/api/architecture-projects/${projectId}`, { method: 'DELETE' });
      toast({ title: "Project Deleted" });
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast({ title: "Error deleting project", variant: "destructive" });
    }
  };

  const handleAddIdea = async () => {
    if (!newIdea.text.trim() || !selectedProject) return;

    try {
      const response = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}/ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newIdea)
      });

      if (response.ok) {
        toast({ title: "Idea Added!" });
        setShowAddIdea(false);
        setNewIdea({ text: '', images: [] });
        
        // Refresh project
        const projRes = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}`);
        if (projRes.ok) {
          const updated = await projRes.json();
          setSelectedProject(updated);
        }
        fetchProjects();
        
        // Send to WhatsApp
        openWhatsApp(`New Idea for ${selectedProject.project_name}:\n\n${newIdea.text}`);
      }
    } catch (error) {
      toast({ title: "Error adding idea", variant: "destructive" });
    }
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!selectedProject) return;

    try {
      await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}/ideas/${ideaId}`, {
        method: 'DELETE'
      });
      toast({ title: "Idea Deleted" });
      
      // Refresh project
      const projRes = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}`);
      if (projRes.ok) {
        const updated = await projRes.json();
        setSelectedProject(updated);
      }
      fetchProjects();
    } catch (error) {
      toast({ title: "Error deleting idea", variant: "destructive" });
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedProject) return;

    try {
      const response = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newMessage, sender_type: 'client' })
      });

      if (response.ok) {
        // Refresh project
        const projRes = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}`);
        if (projRes.ok) {
          const updated = await projRes.json();
          setSelectedProject(updated);
        }
        
        // Send to WhatsApp
        openWhatsApp(`Message for ${selectedProject.project_name}:\n\n${newMessage}`);
        
        setNewMessage('');
        toast({ title: "Message sent!" });
      }
    } catch (error) {
      toast({ title: "Error sending message", variant: "destructive" });
    }
  };

  const handleFileUpload = async (e) => {
    if (!selectedProject) return;
    const files = Array.from(e.target.files || []);
    
    for (const file of files) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const uploadRes = await fetch(`${API_URL}/api/upload`, { method: 'POST', body: formData });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          // Add file to project
          await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}/files`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: file.name, url: uploadData.url })
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
      setUploading(false);
    }

    // Refresh project
    const projRes = await fetch(`${API_URL}/api/architecture-projects/${selectedProject.id}`);
    if (projRes.ok) {
      const updated = await projRes.json();
      setSelectedProject(updated);
    }
    fetchProjects();
    toast({ title: "Files uploaded!" });
  };

  const openWhatsApp = (message) => {
    if (!whatsappNumber) return;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'bg-blue-100 text-blue-700';
      case 'in-progress': return 'bg-amber-100 text-amber-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF8C42]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#404040]">
              Welcome, {clientInfo.name}!
            </h1>
            <p className="text-gray-600">Manage your architecture projects</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => openWhatsApp(`Hi! I'm ${clientInfo.name}. I'd like to discuss my architecture project.`)}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <WhatsAppIcon className="w-5 h-5 mr-2" />
              Chat on WhatsApp
            </Button>
            <Button onClick={handleLogout} variant="outline">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Projects List */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-bold text-lg">Your Projects</h2>
                  <Button size="sm" onClick={() => setShowNewProject(true)} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {projects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No projects yet</p>
                    <Button size="sm" className="mt-3" onClick={() => setShowNewProject(true)}>
                      Create Your First Project
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {projects.map(project => (
                      <button
                        key={project.id}
                        onClick={() => setSelectedProject(project)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedProject?.id === project.id
                            ? 'bg-[#FF8C42]/10 border-2 border-[#FF8C42]'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-[#404040]">{project.project_name}</p>
                            <p className="text-xs text-gray-500 capitalize">{project.project_type?.replace('_', ' ')}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(project.status)}`}>
                            {project.status}
                          </span>
                        </div>
                        <div className="flex gap-3 mt-2 text-xs text-gray-500">
                          <span>{project.ideas?.length || 0} ideas</span>
                          <span>{project.files?.length || 0} files</span>
                          <span>{project.messages?.length || 0} messages</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Project Details */}
          <div className="lg:col-span-2">
            {selectedProject ? (
              <div className="space-y-4">
                {/* Project Header */}
                <Card className="shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(selectedProject.status)}`}>
                            {selectedProject.status}
                          </span>
                          <span className="text-xs text-gray-500 capitalize">
                            {selectedProject.project_type?.replace('_', ' ')}
                          </span>
                        </div>
                        <h2 className="text-2xl font-bold text-[#404040]">{selectedProject.project_name}</h2>
                        {selectedProject.property_address && (
                          <p className="text-gray-600">{selectedProject.property_address}</p>
                        )}
                        {selectedProject.description && (
                          <p className="text-gray-500 mt-2">{selectedProject.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setShowEditProject(true)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteProject(selectedProject.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Ideas Section */}
                <Card className="shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                        Ideas & Inspiration
                      </h3>
                      <Button size="sm" onClick={() => setShowAddIdea(true)} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                        <Plus className="w-4 h-4 mr-1" /> Add Idea
                      </Button>
                    </div>

                    {selectedProject.ideas?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No ideas yet. Share your vision!</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedProject.ideas?.map(idea => (
                          <div key={idea.id} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="flex justify-between items-start">
                              <p className="text-gray-700">{idea.text}</p>
                              <button onClick={() => handleDeleteIdea(idea.id)} className="text-red-500 hover:text-red-700">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            {idea.images?.length > 0 && (
                              <div className="flex gap-2 mt-2">
                                {idea.images.map((img, i) => (
                                  <img key={i} src={img} alt="" className="w-16 h-16 object-cover rounded" />
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {new Date(idea.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Files Section */}
                <Card className="shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-500" />
                        Files & Documents
                      </h3>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,application/pdf,.doc,.docx"
                          multiple
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                        <Button size="sm" asChild className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                          <span>
                            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Upload
                          </span>
                        </Button>
                      </label>
                    </div>

                    {selectedProject.files?.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No files uploaded yet</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {selectedProject.files?.map(file => (
                          <a
                            key={file.id}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 flex items-center gap-2"
                          >
                            <FileText className="w-5 h-5 text-gray-400" />
                            <span className="text-sm truncate">{file.name}</span>
                            <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Messages Section */}
                <Card className="shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                      <MessageSquare className="w-5 h-5 text-purple-500" />
                      Messages
                    </h3>

                    <div className="max-h-64 overflow-y-auto space-y-3 mb-4">
                      {selectedProject.messages?.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No messages yet</p>
                      ) : (
                        selectedProject.messages?.map(msg => (
                          <div
                            key={msg.id}
                            className={`p-3 rounded-lg ${
                              msg.sender_type === 'client'
                                ? 'bg-[#FF8C42]/10 ml-8'
                                : 'bg-gray-100 mr-8'
                            }`}
                          >
                            <p className="text-sm">{msg.text}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {msg.sender_type === 'client' ? 'You' : 'Harry & Jacobs'} • {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <Button onClick={handleSendMessage} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => openWhatsApp(newMessage || `Hi, regarding ${selectedProject.project_name}...`)} className="bg-green-500 hover:bg-green-600">
                        <WhatsAppIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="shadow-lg h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 text-lg">Select a project to view details</p>
                  <p className="text-gray-400 text-sm mt-2">Or create a new project to get started</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Create New Project</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowNewProject(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <Label>Project Name *</Label>
                  <Input
                    value={newProjectForm.project_name}
                    onChange={(e) => setNewProjectForm({...newProjectForm, project_name: e.target.value})}
                    placeholder="e.g., Kitchen Extension"
                    required
                  />
                </div>

                <div>
                  <Label>Project Type *</Label>
                  <select
                    value={newProjectForm.project_type}
                    onChange={(e) => setNewProjectForm({...newProjectForm, project_type: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                    required
                  >
                    <option value="">Select type...</option>
                    <option value="extension">House Extension</option>
                    <option value="loft">Loft Conversion</option>
                    <option value="basement">Basement Conversion</option>
                    <option value="new_build">New Build</option>
                    <option value="renovation">Renovation</option>
                    <option value="interior_design">Interior Design</option>
                    <option value="planning">Planning Application</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label>Property Address</Label>
                  <Input
                    value={newProjectForm.property_address}
                    onChange={(e) => setNewProjectForm({...newProjectForm, property_address: e.target.value})}
                    placeholder="Where is the project?"
                  />
                </div>

                <div>
                  <Label>Budget Range</Label>
                  <select
                    value={newProjectForm.budget_range}
                    onChange={(e) => setNewProjectForm({...newProjectForm, budget_range: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">Select budget...</option>
                    <option value="under_50k">Under £50,000</option>
                    <option value="50k_100k">£50,000 - £100,000</option>
                    <option value="100k_200k">£100,000 - £200,000</option>
                    <option value="200k_500k">£200,000 - £500,000</option>
                    <option value="over_500k">Over £500,000</option>
                  </select>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newProjectForm.description}
                    onChange={(e) => setNewProjectForm({...newProjectForm, description: e.target.value})}
                    placeholder="Tell us about your project..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    Create Project
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewProject(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Idea Modal */}
      {showAddIdea && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Add Idea</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowAddIdea(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Your Idea</Label>
                  <Textarea
                    value={newIdea.text}
                    onChange={(e) => setNewIdea({...newIdea, text: e.target.value})}
                    placeholder="Describe your idea, inspiration, or requirements..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleAddIdea} className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    <Lightbulb className="w-4 h-4 mr-2" /> Save Idea
                  </Button>
                  <Button onClick={() => {
                    handleAddIdea();
                  }} className="bg-green-500 hover:bg-green-600">
                    <WhatsAppIcon className="w-4 h-4 mr-2" /> Save & Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditProject && selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg">Edit Project</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowEditProject(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Project Name</Label>
                  <Input
                    value={selectedProject.project_name}
                    onChange={(e) => setSelectedProject({...selectedProject, project_name: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Status</Label>
                  <select
                    value={selectedProject.status}
                    onChange={(e) => setSelectedProject({...selectedProject, status: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="planning">Planning</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <Label>Property Address</Label>
                  <Input
                    value={selectedProject.property_address || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, property_address: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={selectedProject.description || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, description: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleUpdateProject} className="flex-1 bg-[#FF8C42] hover:bg-[#FF6B35]">
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setShowEditProject(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
